import { X, Copy, Check, Code2, Sparkles } from "lucide-react";
import { useState } from "react";

interface JsonViewPopupProps {
  isOpen: boolean;
  onClose: () => void;
  data: any;
  title: string;
}

export const JsonViewPopup: React.FC<JsonViewPopupProps> = ({
  isOpen,
  onClose,
  data,
  title,
}) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop with enhanced blur */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="relative w-full max-w-6xl max-h-[85vh] animate-in zoom-in-95 duration-300">
        <div className="bg-white rounded-2xl shadow-2xl border-2 border-slate-200/60 overflow-hidden flex flex-col max-h-[85vh]">
          {/* Header */}
          <div className="bg-gradient-to-r from-slate-50 via-indigo-50/50 to-violet-50/50 border-b-2 border-slate-200/60 px-6 py-5 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-violet-100 rounded-xl flex items-center justify-center shadow-sm ring-2 ring-indigo-200/50">
                  <Code2 className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                    {title}
                    <Sparkles className="w-4 h-4 text-indigo-500" />
                  </h2>
                  <p className="text-sm text-slate-500 font-medium mt-0.5">
                    Structured JSON output from AI parsing
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopy}
                  className="px-4 py-2.5 hover:bg-white/80 rounded-xl transition-all duration-200 flex items-center gap-2 border-2 border-slate-200 hover:border-indigo-300 hover:shadow-md group"
                  title="Copy to clipboard"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm text-emerald-600 font-bold">
                        Copied!
                      </span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 text-slate-600 group-hover:text-indigo-600 transition-colors" />
                      <span className="text-sm text-slate-700 font-semibold group-hover:text-indigo-600 transition-colors">
                        Copy JSON
                      </span>
                    </>
                  )}
                </button>
                <button
                  onClick={onClose}
                  className="p-2.5 hover:bg-rose-50 rounded-xl transition-all duration-200 border-2 border-slate-200 hover:border-rose-300 group"
                  title="Close"
                >
                  <X className="w-5 h-5 text-slate-600 group-hover:text-rose-600 transition-colors" />
                </button>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto p-6 bg-gradient-to-br from-slate-50 to-indigo-50/30">
            <div className="relative">
              {/* Decorative corner accent */}
              <div className="absolute -top-2 -left-2 w-20 h-20 bg-gradient-to-br from-indigo-500/10 to-violet-500/10 rounded-full blur-2xl" />
              <div className="absolute -bottom-2 -right-2 w-24 h-24 bg-gradient-to-tl from-violet-500/10 to-indigo-500/10 rounded-full blur-2xl" />

              {/* JSON Display */}
              <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-xl p-6 overflow-x-auto shadow-xl border-2 border-slate-700/50">
                {/* Code header bar */}
                <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-700/50">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-rose-500/80"></div>
                    <div className="w-3 h-3 rounded-full bg-amber-500/80"></div>
                    <div className="w-3 h-3 rounded-full bg-emerald-500/80"></div>
                  </div>
                  <span className="text-xs font-bold text-slate-400 ml-3 tracking-wide">
                    result.json
                  </span>
                  <div className="ml-auto">
                    <span className="px-2 py-1 bg-indigo-500/20 text-indigo-300 text-xs font-bold rounded border border-indigo-500/30">
                      JSON
                    </span>
                  </div>
                </div>

                {/* Syntax highlighted JSON */}
                <pre className="text-sm leading-relaxed font-mono whitespace-pre-wrap overflow-x-auto">
                  <code className="text-emerald-400">
                    {JSON.stringify(data, null, 2)}
                  </code>
                </pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
